import { Card, SeriesLineChart } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { figuresFor, money as pickMoney, units as pickUnits } from "../api/figures";
import type { Currency, ReportingClient } from "../api/reporting";
import { useSalesBasis } from "./basis";

/**
 * The last twelve complete months, one card per currency: money on the left
 * axis, units on the right. Rolling rather than calendar so the line is
 * always twelve points long — a January card with one point on it told you
 * nothing — and January is labelled with its year so the boundary reads.
 *
 * The one panel that follows the basis and scope toggles — a line of net
 * against a grid of billed would be two different stories on one screen — and
 * nothing else. The strip and the comparison are the grid's business; the
 * year's shape is the same whatever periods are lit below it.
 *
 * The current month is not on the line. Unfinished, it always reads as a
 * drop, whatever the month is actually doing; it goes in the header instead,
 * as "to date", so the line ends on the last complete month and the latest
 * number is still on the card. In January, when it is the only month, it
 * stays on the line — one point beats none.
 *
 * One request: the last thirteen calendar months from /aggregate/monthly —
 * twelve complete ones for the line and the current one for the caption.
 * Unenriched: thirteen currency aggregates and nothing else.
 */
export function MonthlyPanels({ client }: { client: ReportingClient }) {
  const query = useQuery({
    queryKey: ["sales-overview", "monthly", 13],
    queryFn: () => client.monthlyByCurrency(13),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="grid gap-4 p-4 md:grid-cols-2 lg:px-6">
      {(["CAD", "USD"] as const).map((currency) => (
        <MonthlyCard key={currency} currency={currency} windows={query.data ?? []} loading={query.isLoading} />
      ))}
    </div>
  );
}

function MonthlyCard({
  currency,
  windows,
  loading,
}: {
  currency: Currency;
  windows: Array<{ key: string; by_currency: Array<{ currency?: Currency; figures?: unknown }> }>;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const { basis, scope, count } = useSalesBasis();

  const points = windows.map((window) => {
    const aggregate = window.by_currency.find((entry) => entry.currency === currency);
    const figures = figuresFor(aggregate as Parameters<typeof figuresFor>[0], scope);
    const [year, month] = window.key.split("-").map(Number) as [number, number];
    const date = new Date(year, month - 1, 1);
    return {
      // January carries its year, so a rolling window shows where the year turned.
      month:
        month === 1
          ? date.toLocaleString(undefined, { month: "short", year: "2-digit" })
          : date.toLocaleString(undefined, { month: "short" }),
      money: pickMoney(figures, basis),
      units: pickUnits(figures, basis, count),
    };
  });
  const current = points.length > 1 ? points[points.length - 1] : undefined;
  const data = current ? points.slice(0, -1) : points;

  const compact = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

  return (
    <Card className={`p-4 ${loading ? "opacity-60" : ""}`}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-[14px] font-semibold text-ink">
          {t("overview.rolling-year", "{{currency}} · last 12 months", { currency })}
        </span>
        <span className="text-[11px] uppercase tracking-wider text-ink-muted">
          {basis === "net" ? t("sales.basis.net", "Net") : t("sales.basis.billed", "Billed")}
          {scope === "issued" ? ` · ${t("sales.scope.issued", "Issued only")}` : ""}
          {count === "items" ? ` · ${t("sales.count.items", "Items")}` : ""}
        </span>
        {current && (
          <span className="basis-full text-[12px] text-ink-muted tabular-nums">
            {t("overview.to-date", "{{month}} to date: {{money}} · {{units}} u", {
              month: current.month,
              money: Math.round(current.money).toLocaleString(),
              units: current.units,
            })}
          </span>
        )}
      </div>
      <SeriesLineChart
        data={data}
        xKey="month"
        height={220}
        series={[
          { key: "money", label: t("product.sales.money", "Money"), axis: "left" },
          { key: "units", label: t("product.sales.units", "Units"), axis: "right" },
        ]}
        formatLeft={(value) => compact.format(value)}
        formatRight={(value) => compact.format(value)}
      />
    </Card>
  );
}
